export type HealthResponse = {
  status: "ok";
  service: string;
  version: string;
  database_ok: boolean;
  rate_limiter: "redis" | "memory";
};

export type RunRequest = {
  code: string;
};

export type RunResponse = {
  ok: boolean;
  stdout: string;
  stderr: string;
  compile_error: string | null;
  duration_ms: number;
  message: string;
  exit_code?: number | null;
};

export type LessonSummary = {
  id: string;
  level: number;
  title: string;
  subtitle: string;
  estimated_minutes: number;
  xp_reward: number;
  unlocks: string[];
};

export type LessonsListResponse = {
  version: string;
  track_id: string;
  track_title: string;
  language: string;
  lessons: LessonSummary[];
};

export type LessonChallenge = {
  prompt: string;
  starter_code: string;
  hints: string[];
};

export type LessonValidationRule = {
  type: string;
  description: string;
  must_match_regex?: string;
  value?: string | number;
  values?: Array<string | number>;
};

export type LessonValidationConfig = {
  compile_required: boolean;
  rules: LessonValidationRule[];
  success_message: string;
  failure_message: string;
};

export type Lesson = {
  id: string;
  level: number;
  title: string;
  subtitle: string;
  estimated_minutes: number;
  xp_reward: number;
  badge_reward: string;
  unlocks: string[];
  explain: string;
  example_code: string;
  challenge: LessonChallenge;
  validation: LessonValidationConfig;
};

export type LessonResponse = {
  lesson: Lesson;
};

export type RuleCheckResult = {
  rule_type: string;
  description: string;
  passed: boolean;
};

export type StreakResponse = {
  current_streak: number;
  longest_streak: number;
  last_activity_date: string | null;
  freeze_tokens: number;
  grace_uses: number;
};

export type BadgeResponse = {
  key: string;
  title: string;
  description: string;
  icon: string | null;
  xp_bonus: number;
  awarded: boolean;
  awarded_at: string | null;
};

export type LessonValidateRequest = {
  code: string;
};

export type LessonValidateResponse = {
  lesson_id: string;
  passed: boolean;
  score: number;
  xp_awarded: number;
  message: string;
  checks: RuleCheckResult[];
  run: RunResponse;
  already_completed?: boolean;
  total_xp?: number | null;
  streak?: StreakResponse | null;
  badges_earned?: BadgeResponse[];
};

export type UserResponse = {
  id: string;
  email: string | null;
  username: string | null;
  is_guest: boolean;
  role: string;
  created_at: string;
};

export type AuthTokenResponse = {
  access_token: string;
  refresh_token: string;
  token_type: "bearer";
  user: UserResponse;
};

export type GuestAuthRequest = {
  nickname?: string | null;
};

export type SignupRequest = {
  email: string;
  username?: string | null;
  password: string;
};

export type LoginRequest = {
  identifier: string;
  password: string;
};

export type RefreshRequest = {
  refresh_token: string;
};

export type ProgressLessonItem = {
  lesson_id: string;
  completed: boolean;
  score: number;
  attempt_count: number;
  updated_at: string;
};

export type ProgressResponse = {
  user: UserResponse;
  total_xp: number;
  completed_lesson_ids: string[];
  lesson_progress: ProgressLessonItem[];
  streak?: StreakResponse | null;
  badges?: BadgeResponse[];
};

export type ClassroomCreateRequest = {
  name: string;
  grade_band?: string | null;
};

export type ClassroomJoinRequest = {
  invite_code: string;
};

export type ClassroomResponse = {
  id: string;
  name: string;
  invite_code: string;
  grade_band: string | null;
  teacher_id: string;
  student_count: number;
  created_at: string;
};

export type ClassroomStudentResponse = {
  user_id: string;
  username: string | null;
  email: string | null;
  is_guest: boolean;
  joined_at: string;
  total_xp: number;
  completed_lessons: number;
  streak_days: number;
};

export type TeacherStudentsResponse = {
  classroom: ClassroomResponse;
  students: ClassroomStudentResponse[];
};

export type MeClassroomResponse = {
  classroom_id: string;
  classroom_name: string;
  invite_code: string;
  role: string;
  teacher_username: string | null;
  teacher_email: string | null;
};

export type KidModeCopyResponse = {
  grade_band: string;
  hero_title: string;
  hero_subtitle: string;
  lesson_intro_prefix: string;
  hint_button_label: string;
  run_button_label: string;
  validate_button_label: string;
  encouragement_short: string[];
};

export type ApiErrorResponse = {
  detail?: string;
};

const API_PROXY_BASE = "/api";
const AUTH_STORAGE_KEY = "rustcode.auth.v1";

const API_BASE = (
  ((import.meta as ImportMeta & { env?: Record<string, string | undefined> })
    .env?.VITE_API_BASE_URL as string | undefined) || API_PROXY_BASE
).replace(/\/+$/, "");

type StoredAuth = {
  accessToken: string;
  refreshToken: string;
};

class ApiError extends Error {
  readonly status: number;
  readonly payload: unknown;

  constructor(message: string, status: number, payload: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.payload = payload;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function safeJsonParse(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function getStoredAuth(): StoredAuth | null {
  if (typeof window === "undefined" || !window.localStorage) return null;
  try {
    const raw = window.localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredAuth>;
    if (
      typeof parsed?.accessToken === "string" &&
      parsed.accessToken &&
      typeof parsed?.refreshToken === "string" &&
      parsed.refreshToken
    ) {
      return {
        accessToken: parsed.accessToken,
        refreshToken: parsed.refreshToken,
      };
    }
    return null;
  } catch {
    return null;
  }
}

function setStoredAuth(tokens: StoredAuth): void {
  if (typeof window === "undefined" || !window.localStorage) return;
  window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(tokens));
}

function clearStoredAuth(): void {
  if (typeof window === "undefined" || !window.localStorage) return;
  window.localStorage.removeItem(AUTH_STORAGE_KEY);
}

async function request<T>(
  path: string,
  init?: RequestInit & { authToken?: string | null },
): Promise<T> {
  const authToken = init?.authToken ?? getStoredAuth()?.accessToken ?? null;

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(init?.headers as Record<string, string> | undefined),
  };

  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers,
  });

  const text = await response.text();
  const payload = text ? safeJsonParse(text) : null;

  if (!response.ok) {
    const message =
      (isRecord(payload) &&
        typeof payload.detail === "string" &&
        payload.detail) ||
      `Request failed with status ${response.status}`;
    throw new ApiError(message, response.status, payload);
  }

  return payload as T;
}

export const authStore = {
  load(): StoredAuth | null {
    return getStoredAuth();
  },

  save(tokens: StoredAuth): void {
    setStoredAuth(tokens);
  },

  clear(): void {
    clearStoredAuth();
  },

  getAccessToken(): string | null {
    return getStoredAuth()?.accessToken ?? null;
  },

  getRefreshToken(): string | null {
    return getStoredAuth()?.refreshToken ?? null;
  },
};

export const api = {
  // Health
  health(): Promise<HealthResponse> {
    return request<HealthResponse>("/healthz", { method: "GET" });
  },

  // Auth
  async authGuest(input: GuestAuthRequest): Promise<AuthTokenResponse> {
    const res = await request<AuthTokenResponse>("/auth/guest", {
      method: "POST",
      body: JSON.stringify(input),
    });
    authStore.save({
      accessToken: res.access_token,
      refreshToken: res.refresh_token,
    });
    return res;
  },

  async authSignup(input: SignupRequest): Promise<AuthTokenResponse> {
    const res = await request<AuthTokenResponse>("/auth/signup", {
      method: "POST",
      body: JSON.stringify(input),
    });
    authStore.save({
      accessToken: res.access_token,
      refreshToken: res.refresh_token,
    });
    return res;
  },

  async authLogin(input: LoginRequest): Promise<AuthTokenResponse> {
    const res = await request<AuthTokenResponse>("/auth/login", {
      method: "POST",
      body: JSON.stringify(input),
    });
    authStore.save({
      accessToken: res.access_token,
      refreshToken: res.refresh_token,
    });
    return res;
  },

  async authRefresh(input?: RefreshRequest): Promise<AuthTokenResponse> {
    const refreshToken = input?.refresh_token ?? authStore.getRefreshToken();
    if (!refreshToken) {
      throw new ApiError("No refresh token available.", 401, {
        detail: "No refresh token available.",
      });
    }

    const res = await request<AuthTokenResponse>("/auth/refresh", {
      method: "POST",
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    authStore.save({
      accessToken: res.access_token,
      refreshToken: res.refresh_token,
    });

    return res;
  },

  logout(): void {
    authStore.clear();
  },

  me(authToken?: string): Promise<UserResponse> {
    return request<UserResponse>("/me", {
      method: "GET",
      authToken: authToken ?? null,
    });
  },

  meProgress(authToken?: string): Promise<ProgressResponse> {
    return request<ProgressResponse>("/me/progress", {
      method: "GET",
      authToken: authToken ?? null,
    });
  },

  meStreak(authToken?: string): Promise<StreakResponse> {
    return request<StreakResponse>("/me/streak", {
      method: "GET",
      authToken: authToken ?? null,
    });
  },

  // Badges / achievements
  badges(authToken?: string): Promise<BadgeResponse[]> {
    return request<BadgeResponse[]>("/badges", {
      method: "GET",
      authToken: authToken ?? null,
    });
  },

  // Lessons
  listLessons(): Promise<LessonsListResponse> {
    return request<LessonsListResponse>("/lessons", { method: "GET" });
  },

  getLesson(lessonId: string): Promise<LessonResponse> {
    return request<LessonResponse>(`/lessons/${encodeURIComponent(lessonId)}`, {
      method: "GET",
    });
  },

  // Code run
  runCode(input: RunRequest, authToken?: string): Promise<RunResponse> {
    return request<RunResponse>("/run", {
      method: "POST",
      body: JSON.stringify(input),
      authToken: authToken ?? null,
    });
  },

  // Validation
  validateLesson(
    lessonId: string,
    input: LessonValidateRequest,
    authToken?: string,
  ): Promise<LessonValidateResponse> {
    return request<LessonValidateResponse>(
      `/lessons/${encodeURIComponent(lessonId)}/validate`,
      {
        method: "POST",
        body: JSON.stringify(input),
        authToken: authToken ?? null,
      },
    );
  },

  // Teacher mode
  teacherCreateClassroom(
    input: ClassroomCreateRequest,
    authToken?: string,
  ): Promise<ClassroomResponse> {
    return request<ClassroomResponse>("/teacher/classrooms", {
      method: "POST",
      body: JSON.stringify(input),
      authToken: authToken ?? null,
    });
  },

  teacherListClassrooms(authToken?: string): Promise<ClassroomResponse[]> {
    return request<ClassroomResponse[]>("/teacher/classrooms", {
      method: "GET",
      authToken: authToken ?? null,
    });
  },

  teacherRotateInviteCode(
    classroomId: string,
    authToken?: string,
  ): Promise<ClassroomResponse> {
    return request<ClassroomResponse>(
      `/teacher/classrooms/${encodeURIComponent(classroomId)}/rotate-invite`,
      {
        method: "POST",
        authToken: authToken ?? null,
      },
    );
  },

  teacherClassroomStudents(
    classroomId: string,
    authToken?: string,
  ): Promise<TeacherStudentsResponse> {
    return request<TeacherStudentsResponse>(
      `/teacher/classrooms/${encodeURIComponent(classroomId)}/students`,
      {
        method: "GET",
        authToken: authToken ?? null,
      },
    );
  },

  // Classroom membership
  joinClassroom(
    input: ClassroomJoinRequest,
    authToken?: string,
  ): Promise<ClassroomResponse> {
    return request<ClassroomResponse>("/classrooms/join", {
      method: "POST",
      body: JSON.stringify(input),
      authToken: authToken ?? null,
    });
  },

  myClassrooms(authToken?: string): Promise<MeClassroomResponse[]> {
    return request<MeClassroomResponse[]>("/classrooms/me", {
      method: "GET",
      authToken: authToken ?? null,
    });
  },

  // Kid mode copy
  kidModeCopy(): Promise<KidModeCopyResponse> {
    return request<KidModeCopyResponse>("/kid-mode/copy", {
      method: "GET",
    });
  },
};

export { ApiError };
